import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, ShoppingBag, ChevronRight, Mail } from "lucide-react";
import { ASSETS, BRAND, TESTIDS, waUrl } from "@/lib/brand";
import { useCart } from "@/hooks/useCart";
import { Drawer } from "./Drawer";
import { CartDrawer } from "./CartDrawer";

export const NAV_LINKS = [
  { label: "A2 Cow Ghee", to: "/a2cow-ghee", id: "nav-a2-cow-ghee" },
  { label: "Forest Honey", to: "/forest-honey", id: "nav-forest-honey" },
  { label: "Why Rudrima", to: "/#why", id: TESTIDS.navWhy },
  { label: "Track Order", to: "/track-order", id: "nav-track-order" },
  { label: "Contact Us", to: "/contact-us", id: TESTIDS.navContact },
];

const DRAWER_LINKS = [
  { label: "A2 Cow Ghee", to: "/a2cow-ghee", sub: "500g · ₹999" },
  { label: "Forest Honey", to: "/forest-honey", sub: "500g · ₹599" },
  { label: "Track Order", to: "/track-order" },
  { label: "Contact Us", to: "/contact-us" },
];
const DRAWER_LEGAL = [
  { label: "Why Rudrima", to: "/#why" }, { label: "Reviews", to: "/#reviews" }, { label: "FAQ", to: "/#faq" },
  { label: "Shipping Policy", to: "/shipping-policy" }, { label: "Refund & Return Policy", to: "/refund-and-return-policy" },
  { label: "Terms & Conditions", to: "/terms-and-conditions" }, { label: "Privacy Policy", to: "/privacy-policy" },
];

const Logo = ({ onClick, testId }) => (
  <Link to="/" onClick={onClick} className="flex items-center gap-2.5 min-h-[44px]" data-testid={testId} aria-label="Rudrima home">
    <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#FFF8E7] border border-[#2E7D32]/20 flex items-center justify-center overflow-hidden shadow-sm">
      <img src={ASSETS.logo} alt="Rudrima" width="44" height="44" className="w-full h-full object-contain" />
    </div>
    <div className="leading-tight">
      <div className="font-display text-xl sm:text-2xl font-bold text-[#2E7D32] tracking-tight">Rudrima</div>
      <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-[#8D6E63] font-semibold">Pure · Natural · Healthy</div>
    </div>
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const { pathname } = useLocation();
  const cart = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setMenu(false); }, [pathname]);

  const isActive = (to) => !to.includes("#") && pathname === to;
  const solid = scrolled || pathname !== "/";

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${solid ? "bg-[#FFF8E7]/88 backdrop-blur-xl border-b border-[#8D6E63]/10 shadow-[0_8px_24px_-16px_rgba(20,60,24,0.25)]" : "bg-transparent"}`} data-testid="site-header">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between h-[60px] sm:h-20 gap-2">
            <button onClick={() => setMenu(true)} data-testid={TESTIDS.hamburgerBtn} className="lg:hidden w-11 h-11 -ml-1 rounded-full flex items-center justify-center active:scale-95 transition" aria-label="Open menu">
              <Menu className="w-6 h-6 text-[#143C18]" strokeWidth={1.8} />
            </button>

            <div className="lg:mr-auto"><Logo testId="header-logo-link" /></div>

            <nav className="hidden lg:flex items-center gap-8 mr-8" aria-label="Main">
              {NAV_LINKS.map((l) => (
                <Link key={l.label} to={l.to} data-testid={l.id} className={`text-sm font-semibold transition-colors relative after:content-[''] after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:bg-[#D4A017] hover:after:w-full after:transition-all ${isActive(l.to) ? "text-[#2E7D32] after:w-full" : "text-[#1A1614] hover:text-[#2E7D32] after:w-0"}`}>
                  {l.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-1 sm:gap-3">
              <div className="hidden sm:block">
                <Link to={pathname === "/a2cow-ghee" || pathname === "/forest-honey" ? `${pathname}#buy` : "/#products"} data-testid={TESTIDS.navOrder} className="btn-primary text-xs sm:text-sm !py-2.5 !px-4 sm:!px-5">
                  <ShoppingBag className="w-4 h-4" /> Order Now
                </Link>
              </div>
              <button onClick={() => setCartOpen(true)} data-testid="header-cart-btn" aria-label={`Open cart, ${cart.count} items`} className="relative w-11 h-11 rounded-full flex items-center justify-center hover:bg-white/60 active:scale-95 transition">
                <ShoppingBag className="w-6 h-6 text-[#143C18]" strokeWidth={1.8} />
                {cart.count > 0 && <span className="absolute top-1 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#2E7D32] text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#FFF8E7]" data-testid="header-cart-count">{cart.count}</span>}
              </button>
            </div>
          </div>
        </div>
      </header>

      <Drawer open={menu} onClose={() => setMenu(false)} side="left" label="Menu" testId={TESTIDS.mobileMenu}>
        <div className="flex items-center justify-between px-4 h-[60px] border-b border-[#8D6E63]/15">
          <Logo onClick={() => setMenu(false)} testId="drawer-logo-link" />
          <button onClick={() => setMenu(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="w-11 h-11 rounded-full flex items-center justify-center hover:bg-[#F6EBCB] active:scale-95 transition"><X className="w-5 h-5 text-[#143C18]" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Mobile" data-lenis-prevent>
          <ul className="space-y-1">
            {DRAWER_LINKS.map((l) => (
              <li key={l.label}>
                <Link to={l.to} onClick={() => setMenu(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className={`flex items-center justify-between px-3 py-3.5 rounded-2xl transition active:scale-[0.98] ${isActive(l.to) ? "bg-[#2E7D32]/10" : "hover:bg-white"}`}>
                  <span>
                    <span className="block font-display text-lg font-bold text-[#143C18]">{l.label}</span>
                    {l.sub && <span className="block text-xs text-[#8D6E63] font-semibold">{l.sub}</span>}
                  </span>
                  <ChevronRight className="w-5 h-5 text-[#D4A017]" />
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-6 pt-5 border-t border-[#8D6E63]/15 px-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#8D6E63] font-bold mb-3">More</div>
            <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
              {DRAWER_LEGAL.map((l) => (
                <li key={l.label}><Link to={l.to} onClick={() => setMenu(false)} className="block py-2.5 text-sm text-[#5D4037] hover:text-[#2E7D32]">{l.label}</Link></li>
              ))}
            </ul>
          </div>
        </nav>
        <div className="px-4 pt-3 pb-4 border-t border-[#8D6E63]/15 bg-[#FBF4E1] space-y-2">
          <a href={waUrl()} target="_blank" rel="noopener noreferrer" data-testid="mobile-menu-whatsapp" className="btn-primary w-full justify-center !py-3.5 !bg-[#25D366] hover:!bg-[#1DA851]"><MessageCircle className="w-5 h-5" /> Chat on WhatsApp</a>
          <a href={`mailto:${BRAND.email}`} className="flex items-center justify-center gap-2 text-xs text-[#5D4037] py-1"><Mail className="w-3.5 h-3.5" /> {BRAND.email}</a>
        </div>
      </Drawer>

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} cart={cart} />
    </>
  );
};
